import React, { memo, useMemo, useState } from 'react';
import { first, last } from 'lodash';
import { ScaleLinear, ScaleTime } from 'd3-scale';
import { Bar, Line } from '@visx/shape';
import { GridColumns, GridRows } from '@visx/grid';
import { AxisLeft, AxisRight } from '@visx/axis';
import { getStringWidth } from '@visx/text';
import { common, model } from '@gosupersimple/types';

import { localPoint } from '@visx/event';

import { notNil } from '@/lib/utils';
import { findClosestDate, getFormattedDateForPeriod } from '@/lib/date';
import { ChartType, TimeAggregationPeriod } from '@/explore/types';
import { formatPropertyValue } from '@/explore/utils/format';
import { GroupFilter } from '@/explore/utils/drilldown';
import { InteractableProps } from '@/explore/utils/interactable';

import { ChartTooltip, ChartTooltipItem, detectHoveredSeries, renderGridColumns } from '../common';
import {
  getHighlightedTickValues,
  getScaleTimeBandwidth,
  getTickValues,
  buildTooltipData,
  getBarSeriesPosition,
} from './utils';
import { isGroupedTimeSeriesData, TimeSeriesData } from '../grouped-chart/types';
import { AreaStack } from './area-stack';
import { LineStack } from './line-stack';
import { BarStack } from './bar-stack';
import { StackValues } from './stack-values';
import {
  explodeCombinedKey,
  getLeftRightAxisFormats,
  getLeftRightAxisTypes,
  getSeriesAxes,
  isCombinedKey,
} from '../grouped-chart/utils';
import { SeriesValueDot } from './series-value-dot';
import { NonOverlappingValueLabels } from '../non-overlapping-value-labels';
import { getScreenPoint } from '../utils';

import { useValueScales } from './use-value-scales';

import commonStyles, {
  gridLineColor,
  gridHighlightedLineColor,
  hoverLineColor,
} from '../charts.module.scss';

const NumYTicks = 7;
const YValuePaddingCoef = 0.2;

export type StackedChartData = {
  [key: string]: number | Date;
  date: Date;
}[];

export interface ChartColor {
  fill: string;
  highlight: string;
}

interface StackedTimeSeriesChartProps {
  width: number;
  height: number;
  data: TimeSeriesData;
  dateScale: ScaleTime<number, number>;
  zonedDateScale: ScaleTime<number, number>;
  aggPeriod: TimeAggregationPeriod | null;
  highlightedDate: Date | null;
  highlightedSeriesKey?: string | null;
  groupKey: string | undefined;
  onTimeClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  onDateHighlight: (date: Date | null) => void;
  hideGrid: boolean;
  timezone: string;
  showTotals: boolean;
}

export const StackedTimeSeriesChart = (props: StackedTimeSeriesChartProps) => {
  const {
    width,
    height,
    data,
    dateScale,
    zonedDateScale,
    aggPeriod,
    groupKey,
    onTimeClick,
    onDateHighlight,
    hideGrid,
    timezone,
    showTotals,
    highlightedDate,
    highlightedSeriesKey,
  } = props;

  const hasOnlyBars = data.series.every(({ chartType }) => chartType === 'bar');
  const hasBars = hasOnlyBars || data.series.some(({ chartType }) => chartType === 'bar');

  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  const { axes, axisTypes, axisFormats } = useMemo(
    () => ({
      axes: getSeriesAxes(data.series),
      axisTypes: getLeftRightAxisTypes(data.series),
      axisFormats: getLeftRightAxisFormats(data.series),
    }),
    [data.series],
  );

  const valueScales = useValueScales({
    data,
    height,
    padding: YValuePaddingCoef,
    numTicks: NumYTicks,
    stacked: true,
  });

  const handleMouseOut = () => {
    onDateHighlight(null);
    setTooltipData(null);
  };

  const formatLeftAxisValue = (n: number) => {
    const value = axisTypes.left === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.left,
      format: axisFormats.left,
      style: 'compact',
    });
  };

  const formatRightAxisValue = (n: number) => {
    const value = axisTypes.right === 'Integer' ? Math.round(n) : Math.round(n * 100) / 100;
    return formatPropertyValue(value, {
      type: axisTypes.right,
      format: axisFormats.right,
      style: 'compact',
    });
  };

  const yAxisWidths = {
    left: Math.max(
      ...valueScales.left
        .ticks(NumYTicks)
        .map((i) => formatLeftAxisValue(i.valueOf()))
        .map((i) => getStringWidth(i) ?? 0),
    ),
  };

  const domainSizes = {
    left: valueScales.left.domain()[1] - valueScales.left.domain()[0],
    right: valueScales.right.domain()[1] - valueScales.right.domain()[0],
  };

  const hasRightAxisSeries = data.series.some((series) => series.axis === 'right');

  // Default to right as main axis in case no series on left axis
  const primaryValueScale = domainSizes.left > 0 ? valueScales.left : valueScales.right;

  // Force right axis ticks to align with primary axis ticks
  const rightAxisTicks = primaryValueScale
    .ticks(NumYTicks)
    .map((tick) => valueScales.right.invert(primaryValueScale(tick)));

  // Hovered series detection
  const handleMouseMove = (
    event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>,
  ) => {
    const point = localPoint(event);
    if (!point) {
      return setTooltipData(null);
    }

    const dates = data.items.map((item) => item.dateValue);
    if (dates.length === 0) {
      return setTooltipData(null);
    }

    // For bar charts, find which bar we're actually hovering over based on X position
    // Don't use date snapping as it can jump to wrong dates when hovering bar edges
    const { barWidth, x: barOffset } = getBarSeriesPosition(dateScale, aggPeriod, 1, 0);
    let closestDate: Date | undefined;

    // Check each date's bar to see if mouse X is within it
    for (const date of dates) {
      const barX = dateScale(date) + barOffset;
      if (point.x >= barX && point.x <= barX + barWidth) {
        closestDate = date;
        break;
      }
    }

    // Fallback to closest date if not over any bar (for non-bar series or edges)
    if (!closestDate) {
      const xTickSize = width / dates.length;
      const hoveredDate = new Date(dateScale.invert(point.x - xTickSize / 2));
      closestDate = findClosestDate(dates, hoveredDate) ?? last(dates);
    }

    if (!closestDate) {
      return setTooltipData(null);
    }

    // Find the data item for this date to get series values
    const dataItem = data.items.find((item) => item.dateValue.getTime() === closestDate.getTime());
    if (dataItem === undefined) {
      return setTooltipData(null);
    }

    // Handle both regular and grouped data
    const values = Array.isArray(dataItem.values) ? {} : dataItem.values;
    const seriesData = data.series.map((s) => ({
      key: s.key,
      value: (values as Record<string, number | null>)[s.key] ?? null,
      chartType: s.chartType,
      axis: s.axis,
    }));

    // Calculate bar positions for bar detection
    const barSeries = seriesData.filter((s) => s.chartType === 'bar');
    let barPositions: Array<{ key: string; x: number; width: number }> = [];

    if (barSeries.length > 0) {
      // For stacked bars, all segments share the same X position and width
      // So we only need one position entry, not one per series
      barPositions = [
        {
          key: barSeries[0].key, // Use first series key, but this represents the whole stack
          x: dateScale(closestDate) + barOffset,
          width: barWidth,
        },
      ];
    }

    const hoveredSeriesKey = detectHoveredSeries(
      point.y,
      seriesData,
      valueScales,
      point.x,
      barPositions,
      true, // isStacked
    );

    onDateHighlight(closestDate);

    setTooltipData(
      buildTooltipData(
        dataItem,
        getScreenPoint(point, event.target as SVGElement),
        data,
        timezone,
        aggPeriod,
        hoveredSeriesKey,
      ),
    );
  };

  const renderBarStacks = (chartData: TimeSeriesData) => {
    const width = dateScale.range()[1];
    const barGroupWidth = width / chartData.items.length;
    const barGroupInnerWidth = barGroupWidth * 0.7;
    const barWidth = barGroupInnerWidth / axes.length;
    const barInnerWidth = barWidth * 0.8;

    return axes.map((axis, idx) => {
      const offset = -(barGroupInnerWidth / 2) + idx * barWidth + barWidth / 2;
      return (
        <AxisBarStack
          key={`bar-stack-${axis}`}
          axis={axis}
          chartData={chartData}
          dateScale={dateScale}
          valueScale={valueScales[axis]}
          showTotals={showTotals}
          offset={offset}
          barGroupWidth={barGroupWidth}
          barWidth={barInnerWidth}
          highlightKey={highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey}
        />
      );
    });
  };

  const renderHighlightedPeriod = () => {
    if (!hasBars || !highlightedDate) {
      return null;
    }

    const bandwidth = getScaleTimeBandwidth(dateScale, aggPeriod);
    return (
      <rect
        x={dateScale(highlightedDate) - bandwidth / 2}
        y={0}
        width={bandwidth}
        height={height}
        fill={gridLineColor}
        pointerEvents="none"
      />
    );
  };

  const renderHighlightedDate = () => {
    if (!highlightedDate) {
      return null;
    }

    return (
      <>
        {hasOnlyBars ? null : (
          <Line
            from={{ x: dateScale(highlightedDate), y: 0 }}
            to={{ x: dateScale(highlightedDate), y: height }}
            stroke={hoverLineColor}
            strokeWidth={1}
            pointerEvents="none"
            strokeDasharray="5,2"
          />
        )}
        {tooltipData !== null &&
          tooltipData?.points.map((point, i) => {
            if (point.date === null || point.chartType === 'bar') {
              return null;
            }

            const value = Number(
              tooltipData?.points
                .slice(i)
                .filter(({ axis }) => axis === point.axis)
                .reduce(
                  (sum, p) => (point.chartType === p.chartType ? sum + (p.value ?? 0) : sum),
                  0,
                ),
            );

            return (
              <circle
                key={i}
                cx={dateScale(point.date)}
                cy={valueScales[point.axis](value)}
                r={5}
                fill={point.color}
                stroke="white"
                strokeOpacity={0.2}
                strokeWidth={1}
                pointerEvents="none"
              />
            );
          })}
      </>
    );
  };

  const renderSeriesValueDots = () => {
    // Only render when there is a single date value
    if (data.items.length !== 1) {
      return null;
    }
    // Only render when there is no grouping
    if (isGroupedTimeSeriesData(data)) {
      return null;
    }
    // Only render dots for line and area series
    const series = data.series
      .filter(({ chartType }) => chartType === 'line' || chartType === 'area')
      .reverse();
    return data.items.map((item) => {
      let cumValue = 0;
      return series.map(({ key, axis, color }) => {
        cumValue += item.values[key] ?? 0;
        return (
          <SeriesValueDot
            key={key}
            cx={dateScale(item.dateValue)}
            cy={valueScales[axis](cumValue)}
            color={color}
          />
        );
      });
    });
  };

  return (
    <div className={commonStyles.graph}>
      <svg width={width} height={height} className={commonStyles.graphMain}>
        {hideGrid ? null : (
          <>
            <GridRows
              width={width}
              height={height}
              scale={primaryValueScale}
              stroke={gridLineColor}
              tickValues={primaryValueScale.ticks(NumYTicks)}
            />
            <GridColumns
              width={width}
              height={height}
              scale={zonedDateScale}
              stroke={gridLineColor}
              tickValues={getTickValues(aggPeriod ?? 'hour', zonedDateScale)}
              // eslint-disable-next-line react/no-children-prop
              children={(lines) => renderGridColumns(lines, { width, strokeColor: gridLineColor })}
            />
            <GridColumns
              width={width}
              height={height}
              scale={zonedDateScale}
              stroke={gridHighlightedLineColor}
              tickValues={getHighlightedTickValues(aggPeriod ?? 'hour', zonedDateScale)}
              // eslint-disable-next-line react/no-children-prop
              children={(lines) =>
                renderGridColumns(lines, { width, strokeColor: gridHighlightedLineColor })
              }
            />
          </>
        )}

        {axes.map((axis) => {
          const areaKeys = data.series
            .filter((s) => s.chartType === 'area' && s.axis === axis)
            .map(({ key }) => key);
          const lineKeys = data.series
            .filter((s) => s.chartType === 'line' && s.axis === axis)
            .map(({ key }) => key);

          return domainSizes[axis] <= 0 ? null : (
            <React.Fragment key={axis}>
              <AreaStack
                keys={areaKeys}
                data={data}
                valueScale={valueScales[axis]}
                dateScale={dateScale}
                highlightKey={highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey}
              />

              <LineStack
                keys={lineKeys}
                data={data}
                valueScale={valueScales[axis]}
                dateScale={dateScale}
                highlightKey={highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey}
              />

              <NonOverlappingValueLabels>
                <StackValues
                  keys={areaKeys}
                  data={data}
                  valueScale={valueScales[axis]}
                  dateScale={dateScale}
                  avoidOverflow
                  showTotals={showTotals}
                />
                <StackValues
                  keys={lineKeys}
                  data={data}
                  valueScale={valueScales[axis]}
                  dateScale={dateScale}
                  avoidOverflow
                  showTotals={showTotals}
                />
              </NonOverlappingValueLabels>
            </React.Fragment>
          );
        })}

        {renderBarStacks(data)}
        {renderSeriesValueDots()}

        {hideGrid ? null : (
          <>
            {domainSizes.left > 0 && (
              <AxisLeft
                left={yAxisWidths.left + 4}
                scale={valueScales.left}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 4, dy: 10, textAnchor: 'end' })}
                tickValues={valueScales.left.ticks(NumYTicks)}
                tickFormat={(n) => formatLeftAxisValue(n.valueOf())}
              />
            )}
            {domainSizes.right > 0 && hasRightAxisSeries && (
              <AxisRight
                left={width - 4}
                scale={valueScales.right}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 0, dy: 10, x: 0, textAnchor: 'end' })}
                tickValues={rightAxisTicks}
                tickFormat={(n) => formatRightAxisValue(n.valueOf())}
              />
            )}
          </>
        )}
      </svg>

      <svg width={width} height={height} className={commonStyles.graphInteractive}>
        {renderHighlightedPeriod()}
        {renderHighlightedDate()}
        {
          <Bar
            {...InteractableProps}
            width={width}
            height={height}
            fill="transparent"
            onTouchStart={handleMouseMove}
            onTouchMove={handleMouseMove}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseOut}
            onClick={(e) => {
              if (!tooltipData || groupKey === undefined) {
                return;
              }
              const { clientX, clientY } = e;
              const date = tooltipData.date.toISOString();
              const series = data.series[0];
              onTimeClick?.({
                x: clientX,
                y: clientY,
                key: isCombinedKey(series.key)
                  ? explodeCombinedKey(series.key).valueKey
                  : series.key,
                groupFilters: [
                  {
                    key: groupKey,
                    value: date,
                    precision: aggPeriod ?? undefined,
                  },
                ],
              });
            }}
          />
        }
      </svg>

      {tooltipData && <Tooltip tooltipData={tooltipData} onDismiss={() => setTooltipData(null)} />}
    </div>
  );
};

const AxisBarStack = memo(function AxisBarStack(props: {
  axis: 'left' | 'right';
  chartData: TimeSeriesData;
  dateScale: ScaleTime<number, number>;
  valueScale: ScaleLinear<number, number>;
  showTotals: boolean;
  offset: number;
  barGroupWidth: number;
  barWidth: number;
  highlightKey?: string;
}) {
  const {
    axis,
    chartData,
    dateScale,
    valueScale,
    showTotals,
    offset,
    barGroupWidth,
    barWidth,
    highlightKey,
  } = props;

  const keys = useMemo(
    () =>
      chartData.series
        .filter((s) => s.chartType === 'bar' && s.axis === axis)
        .map(({ key }) => key),
    [axis, chartData.series],
  );

  const axisDateScale = useMemo(
    () => dateScale.copy().range([dateScale.range()[0] + offset, dateScale.range()[1] + offset]),
    [dateScale, offset],
  );

  return (
    <>
      <BarStack
        keys={keys}
        data={chartData}
        valueScale={valueScale}
        dateScale={axisDateScale}
        offset={-(barGroupWidth / 2)}
        barWidth={barWidth}
        highlightKey={highlightKey}
      />
      <NonOverlappingValueLabels>
        <StackValues
          keys={keys}
          data={chartData}
          valueScale={valueScale}
          dateScale={axisDateScale}
          bar
          showTotals={showTotals}
        />
      </NonOverlappingValueLabels>
    </>
  );
});

type TooltipData = {
  date: Date;
  position: { x: number; y: number };
  timezone: string;
  aggPeriod: TimeAggregationPeriod | null;
  hoveredSeriesKey?: string;
  points: {
    label: string;
    value: number | null;
    type: model.PropertyType | null;
    format?: common.PropertyValueFormat;
    date: Date;
    color: string;
    axis: 'left' | 'right';
    chartType: ChartType;
    isHovered?: boolean;
  }[];
};

const Tooltip = (props: { tooltipData: TooltipData; onDismiss: () => void }) => {
  const { tooltipData, onDismiss } = props;

  if (tooltipData.points.length === 0) {
    return null;
  }

  const totalValueType = first(tooltipData.points)?.type ?? 'Number';
  const totalValueFormat = tooltipData.points.every(
    (point) => point.format === first(tooltipData.points)?.format,
  )
    ? first(tooltipData.points)?.format
    : undefined;

  return (
    <ChartTooltip
      position={tooltipData.position}
      onDismiss={onDismiss}
      title={getFormattedDateForPeriod(
        tooltipData.date,
        tooltipData.aggPeriod,
        tooltipData.timezone,
      )}>
      <ul>
        {tooltipData.points.length > 1 && (
          <li>
            Total:{' '}
            <strong>
              {formatPropertyValue(
                tooltipData.points
                  .map((point) => point.value)
                  .filter(notNil)
                  .reduce((sum, value) => sum + value, 0),
                { type: totalValueType, format: totalValueFormat },
              )}
            </strong>
          </li>
        )}
        {tooltipData.points.map((point, i) => (
          <ChartTooltipItem
            key={i}
            label={point.label ?? '-'}
            value={formatPropertyValue(point.value, { type: point.type, format: point.format })}
            color={point.color}
            isHovered={point.isHovered}
          />
        ))}
      </ul>
    </ChartTooltip>
  );
};
